import { format } from "date-fns";
import { getCurrentOperationId } from "@entrilia/shared/logging";
import { sanitizeFileSrcForTelemetry } from "@entrilia/shared/utilities/urlTelemetry";
import { resolveClientCode } from "./accessCheckClientCode";
import { isAzureAuthoredFailure } from "./isAzureAuthoredFailure";
import type { DocumentFailureOrigin } from "./judgeDocumentFailure";
import type { BucketClassification, ConnectionCheckResult, DocumentLoadFailure } from "./types";

interface BuildArgs {
  checks: ConnectionCheckResult[];
  classification: BucketClassification;
  email: string | undefined;
  hasInvestorPortalAccess?: boolean;
  documentFailureOrigin?: DocumentFailureOrigin;
  documentLoadError?: DocumentLoadFailure;
}

interface BuiltReport {
  filename: string;
  content: string;
  blob: Blob;
}

const readB2CErrorParams = (): Record<string, string> => {
  const out: Record<string, string> = {};
  if (typeof window === "undefined") {
    return out;
  }
  const collect = (source: string) => {
    if (!source) {
      return;
    }
    const params = new URLSearchParams(source.startsWith("?") || source.startsWith("#") ? source.slice(1) : source);
    for (const key of ["error", "error_description"]) {
      const value = params.get(key);
      if (value && !out[key]) {
        out[key] = value;
      }
    }
  };
  collect(window.location.search);
  collect(window.location.hash);
  return out;
};

const checkLine = (check: ConnectionCheckResult): string => {
  const status = check.ok ? "PASS" : "FAIL";
  const error = check.errorMessage ? ` (${check.errorMessage})` : "";
  return `  - [${status}] ${check.label} → ${check.targetHost} (${check.durationMs}ms)${error}`;
};

const BODY_EXCERPT_LIMIT = 500;

const failureOriginLabels: Record<DocumentFailureOrigin, string> = {
  "azure-authored": "Azure Storage",
  "network-blocked": "not Azure (likely a network security gateway)",
  unknown: "unknown",
};

const failureDetailLines = (check: ConnectionCheckResult, documentFailureOrigin?: DocumentFailureOrigin): string[] => {
  const details = check.failureDetails;
  if (check.ok || !details) {
    return [];
  }
  const lines: string[] = [`  ${check.label} → ${check.targetHost}`];
  if (details.url) {
    // The report is shared with fund managers — never include a live SAS signature.
    lines.push(`    URL: ${sanitizeFileSrcForTelemetry(details.url)}`);
  }
  if (details.statusCode !== undefined) {
    lines.push(`    Status: ${details.statusCode}${details.statusText ? ` ${details.statusText}` : ""}`);
  }
  if (details.errorName || details.errorMessage) {
    lines.push(`    Error: ${[details.errorName, details.errorMessage].filter(Boolean).join(": ")}`);
  }
  Object.entries(details.headers ?? {}).forEach(([key, value]) => lines.push(`    Header: ${key}: ${value}`));
  if (details.body) {
    const excerpt = details.body.slice(0, BODY_EXCERPT_LIMIT);
    const truncated = details.bodyTruncated || details.body.length > BODY_EXCERPT_LIMIT;
    lines.push(`    Body${truncated ? " (truncated)" : ""}: ${excerpt}`);
  }
  if (check.id === "document") {
    // Prefer the judged origin so the report never contradicts the message shown in the UI.
    const label =
      documentFailureOrigin !== undefined
        ? failureOriginLabels[documentFailureOrigin]
        : failureOriginLabels[isAzureAuthoredFailure(details) ? "azure-authored" : "network-blocked"];
    lines.push(`    Authored by: ${label}`);
  }
  return lines;
};

export const buildAccessCheckReportLines = ({
  checks,
  classification,
  email,
  hasInvestorPortalAccess,
  documentFailureOrigin,
  documentLoadError,
}: BuildArgs): string[] => {
  const now = new Date();
  const errorParams = readB2CErrorParams();
  const operationId = getCurrentOperationId() ?? "(not available)";
  const lines: string[] = [];

  lines.push("Entrilia Investor Portal — Access Check Report");
  lines.push("================================================");
  lines.push("");
  lines.push("OUTCOME");
  lines.push(`  Bucket: ${classification.bucket} — ${classification.title}`);
  if (hasInvestorPortalAccess !== undefined) {
    lines.push(`  Has Investor Portal access: ${hasInvestorPortalAccess ? "yes" : "no"}`);
  }
  lines.push("");
  lines.push("SUGGESTION");
  if (classification.conclusion) {
    lines.push(`  ${classification.conclusion}`);
  }
  lines.push(`  ${classification.suggestion}`);
  if (classification.bullets.length > 0) {
    lines.push("");
    lines.push("WHAT TO DO");
    classification.bullets.forEach((bullet) => lines.push(`  - ${bullet}`));
  }
  if (classification.blockedHosts.length > 0) {
    lines.push("");
    lines.push("HOSTS FOR IT TO ALLOW");
    classification.blockedHosts.forEach((host) => lines.push(`  ${host}`));
  }
  lines.push("");
  lines.push("CONNECTION CHECKS");
  checks.forEach((check) => lines.push(checkLine(check)));
  lines.push("");
  if (documentLoadError) {
    lines.push("DOCUMENT LOAD ERROR (as seen by the viewer)");
    lines.push(`  Error: ${documentLoadError.errorName}: ${documentLoadError.errorMessage}`);
    if (documentLoadError.statusCode !== undefined) {
      lines.push(`  Status: ${documentLoadError.statusCode}`);
    }
    lines.push("");
  }
  const detailLines = checks.flatMap((check) => failureDetailLines(check, documentFailureOrigin));
  if (detailLines.length > 0) {
    lines.push("FAILURE DETAILS");
    detailLines.forEach((line) => lines.push(line));
    lines.push("");
  }
  lines.push("SESSION");
  lines.push(`  Email: ${email ?? "(unknown)"}`);
  lines.push(`  Client code: ${resolveClientCode() ?? "(unknown)"}`);
  lines.push(`  Language: ${typeof navigator !== "undefined" ? navigator.language : "(unknown)"}`);
  lines.push(`  Timestamp UTC: ${now.toISOString()}`);
  lines.push(`  Timestamp local: ${format(now, "yyyy-MM-dd HH:mm:ss XXX")}`);
  lines.push("");
  lines.push("USER AGENT");
  lines.push(`  ${typeof navigator !== "undefined" ? navigator.userAgent : "(unknown)"}`);
  lines.push("");
  lines.push("APP INSIGHTS");
  lines.push(`  Operation ID: ${operationId}`);
  if (Object.keys(errorParams).length > 0) {
    lines.push("");
    lines.push("B2C ERROR PARAMS");
    Object.entries(errorParams).forEach(([key, value]) => lines.push(`  ${key}: ${value}`));
  }
  lines.push("");

  return lines;
};

export const buildAccessCheckReport = (args: BuildArgs): BuiltReport => {
  const content = buildAccessCheckReportLines(args).join("\n");
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const filename = `entrilia-access-check-${format(new Date(), "yyyy-MM-dd-HHmmss")}.txt`;
  return { filename, content, blob };
};
